import React from 'react';
import { useLang } from '../context/LanguageContext';
import { PageHeader } from '../components/PageHeader';
import ContactStrip from '../components/ContactStrip';
import SEO from '../components/SEO';
import { SEO_KEYWORDS } from '../data/seo';
import Breadcrumbs from '../components/geo/Breadcrumbs';
import AuthorMeta from '../components/geo/AuthorMeta';
import RelatedTopics from '../components/geo/RelatedTopics';
import { ENTITY } from '../data/geo';
import { SITE_URL, CONTACT } from '../data/site';

const DE = {
  label: 'Über die Plattform', title: 'Über metakaolin.info',
  blocks: [
    { h: 'Zweck der Plattform', p: 'metakaolin.info bündelt wissenschaftlich orientierte, herstellerunabhängig nachvollziehbare Fachinformationen zu Metakaolin – von den Grundlagen über die puzzolanische Reaktion und Qualitätsbewertung bis zu Dauerhaftigkeit, Klinkerreduktion und Anwendungen in Beton und Mörtel.' },
    { h: 'Fachlicher Anspruch', p: 'Inhalte werden sachlich, präzise und technisch belastbar formuliert. Aussagen sind auf konkrete Fragestellungen ausgerichtet und im Kontext von Rezeptur, Bindemittelsystem, w/b-Wert, Zusatzmitteln und Nachbehandlung einzuordnen. Auf unbelegte Superlative wird verzichtet.' },
    { h: 'Zielgruppen', p: 'Die Plattform richtet sich an technische Entscheider und Fachleute aus Beton-, Fertigteil- und Trockenmörtelindustrie, an Planer, Labore sowie an Forschung und Lehre.' },
    { h: 'Rolle von Newchem als technischer Herausgeber', p: 'Herausgeber ist die NEWCHEM GmbH, ein unabhängiger österreichischer Spezialist für mineralische Zusatzstoffe und Bindemittelsysteme. Newchem verantwortet die fachliche Prüfung der Inhalte.' },
    { h: 'Allgemeines Fachwissen und produktbezogene Information', p: 'Allgemeines Fachwissen zu Metakaolin wird klar von produktbezogenen Informationen getrennt. Produktbezogene Angaben beziehen sich ausschließlich auf freigegebene Newchem-Produkte.' },
    { h: 'Umgang mit Quellen und Versuchen', p: 'Technische Aussagen werden als allgemeines Fachwissen, als Newchem-Laborergebnis oder als projektbezogene Daten gekennzeichnet. Versuchsdaten werden mit Methodik, Prüfalter und Randbedingungen ausgewiesen; Vergleichssysteme werden anonymisiert dargestellt.' },
    { h: 'Eignungsprüfung erforderlich', p: 'Die dargestellten Informationen ersetzen keine anwendungsspezifische Eignungsprüfung. Die Übertragbarkeit auf andere Ausgangsstoffe und Rezepturen ist jeweils projektspezifisch zu bewerten.' },
  ],
};
const EN = {
  label: 'About the platform', title: 'About metakaolin.info',
  blocks: [
    { h: 'Purpose of the platform', p: 'metakaolin.info consolidates scientifically oriented, verifiable technical information on metakaolin – from fundamentals via the pozzolanic reaction and quality assessment to durability, clinker reduction and applications in concrete and mortar.' },
    { h: 'Technical standard', p: 'Content is written factually, precisely and in a technically robust manner. Statements address specific questions and must be interpreted in the context of mix design, binder system, w/b ratio, admixtures and curing. Unsubstantiated superlatives are avoided.' },
    { h: 'Target audiences', p: 'The platform addresses technical decision-makers and specialists from the concrete, precast and dry-mortar industries, as well as planners, laboratories, research and education.' },
    { h: 'Newchem as technical publisher', p: 'The publisher is NEWCHEM GmbH, an independent Austrian specialist for mineral additions and binder systems. Newchem is responsible for the technical review of the content.' },
    { h: 'General knowledge vs. product information', p: 'General technical knowledge about metakaolin is clearly separated from product-related information. Product-related statements refer exclusively to approved Newchem products.' },
    { h: 'Handling of sources and tests', p: 'Technical statements are labelled as general knowledge, Newchem laboratory results or project-related data. Test data are reported with methodology, test age and boundary conditions; comparison systems are shown anonymised.' },
    { h: 'Suitability testing required', p: 'The information provided does not replace application-specific suitability testing. Transferability to other raw materials and mixes must be assessed per project.' },
  ],
};

const UeberMetakaolinInfo = () => {
  const { lang } = useLang();
  const d = lang === 'de' ? DE : EN;
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'AboutPage',
    name: d.title, description: ENTITY[lang].description, inLanguage: lang,
    mainEntityOfPage: `${SITE_URL}/ueber-metakaolin-info`,
    publisher: { '@type': 'Organization', name: 'NEWCHEM GmbH', url: CONTACT.website },
  };
  return (
    <div>
      <SEO title={d.title} description={ENTITY[lang].description} path="/ueber-metakaolin-info" lang={lang} keywords={SEO_KEYWORDS[lang]} jsonLd={jsonLd} />
      <PageHeader label={d.label} title={d.title} lead={ENTITY[lang].description} imageKey="labTest2" />
      <section className="bg-white py-14 lg:py-20">
        <div className="container-wide">
          <Breadcrumbs items={[{ label: d.label, to: '/ueber-metakaolin-info' }]} />
          <div className="mt-8 max-w-2xl space-y-10">
            {d.blocks.map((b, i) => (
              <div key={i}>
                <h2 className="text-[22px] font-extrabold tracking-tight text-slate-900">{b.h}</h2>
                <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{b.p}</p>
              </div>
            ))}
            <AuthorMeta dates={{ published: '2025-07-01', reviewed: '2025-07-16' }} />
            <RelatedTopics items={[
              { de: 'Fachredaktion & Expertise', en: 'Editorial team & expertise', to: '/fachredaktion' },
              { de: 'Newchem Kompetenz', en: 'Newchem competence', to: '/newchem-kompetenz' },
              { de: 'Grundlagen', en: 'Fundamentals', to: '/grundlagen' },
            ]} />
          </div>
        </div>
      </section>
      <ContactStrip />
    </div>
  );
};

export default UeberMetakaolinInfo;
